import { Alert, Anchor, Badge, Center, Divider, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { CircleAlert, ExternalLink } from 'lucide-react';
import Markdown from 'react-markdown';
import { Modal } from '@/components/ui';
import { formatChangePct, formatMarketCap, formatPrice } from '@/lib/format';
import { useTickerSummary } from '@/hooks/useTickerSummary';
import { BuzzBadge } from '@/components/shared/BuzzBadge';
import type { TickerRow } from '@/hooks/useTickers';

interface TickerDetailModalProps {
  ticker: TickerRow | null;
  onClose: () => void;
}

function changeColour(value: number | null): string {
  if (value === null || value === 0) return 'dimmed';
  return value > 0 ? 'teal' : 'red';
}

export function TickerDetailModal({ ticker, onClose }: TickerDetailModalProps) {
  const { data, isLoading, isError, error } = useTickerSummary(ticker?.id ?? null);

  return (
    <Modal
      opened={ticker !== null}
      onClose={onClose}
      title={
        ticker ? (
          <Group gap="xs" wrap="nowrap">
            <Title order={3} size="h4">{ticker.symbol}</Title>
            <Text c="dimmed" size="sm" lineClamp={1}>{ticker.name}</Text>
          </Group>
        ) : null
      }
      size="lg"
    >
      {ticker && (
        <Stack gap="md">
          <Group gap="xl" wrap="wrap">
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Price</Text>
              <Text fw={700} size="lg">{formatPrice(ticker.last_price)}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Change</Text>
              <Text fw={600} c={changeColour(ticker.last_price_change_pct)}>
                {formatChangePct(ticker.last_price_change_pct)}
              </Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Market cap</Text>
              <Text fw={600}>{formatMarketCap(ticker.market_cap)}</Text>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Buzz</Text>
              <BuzzBadge score={ticker.buzz_score} />
            </Stack>
          </Group>

          <Divider />

          <Stack gap="sm">
            <Group gap="xs" align="center">
              <Text fw={600}>AI Summary</Text>
              <Badge size="xs" variant="outline" color="blue">AI</Badge>
            </Group>

            {isLoading && (
              <Center py="xl">
                <Stack align="center" gap="xs">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">Generating summary…</Text>
                </Stack>
              </Center>
            )}

            {isError && (
              <Alert color="orange" icon={<CircleAlert size={16} aria-hidden />}>
                {error instanceof Error && error.message === 'ai_failed'
                  ? 'AI summary unavailable right now — the model may be warming up. Try again in a moment.'
                  : error instanceof Error && error.message === 'no_news'
                    ? 'No recent news found for this ticker.'
                    : 'Could not generate summary. Please try again.'}
              </Alert>
            )}

            {data && (
              <Stack gap="md">
                <div className="prose-sm">
                  <Markdown>{data.summary_markdown}</Markdown>
                </div>

                {data.sources.length > 0 && (
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Sources</Text>
                    {data.sources.map((src) => (
                      <Group key={src.id} gap="xs" wrap="nowrap" align="flex-start">
                        <ExternalLink size={13} style={{ marginTop: 3, flexShrink: 0 }} aria-hidden />
                        <Anchor href={src.url} target="_blank" rel="noopener noreferrer" size="sm" lineClamp={2}>
                          {src.title}
                        </Anchor>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      )}
    </Modal>
  );
}
